import { Link, useRouter } from "@shared/client/navigation";
import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Check, ChevronDown, ChevronRight, Copy, ExternalLink, LoaderCircle, MailSearch, RefreshCw } from "lucide-react";
import type { ProspectBoardItem, ProspectDetail } from "@shared/lib/prospects-shared";
import { useView } from "@shared/client/view";
import { cn } from "@shared/lib/utils";
import { AdminEmpty, AdminPanel, StatusDot } from "@shared/components/blocks";
import { LinkedInIcon } from "@shared/components/brand-icons";
import { ContactAvatar, linkedinSearchUrl } from "@shared/components/contact";
import { OrgLogo } from "@shared/components/org-logo";
import { Button } from "@shared/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@shared/components/ui/collapsible";
import { Input } from "@shared/components/ui/input";
import { Textarea } from "@shared/components/ui/textarea";
import { ContactPanel, LeadsPanel, OfferPanel } from "@/components/admin/prospect-workspace";

/**
 * Étape « À vérifier » de /admin/prospection : un dossier à la fois. À gauche
 * les dossiers préparés par l'agent ; à droite ce qu'il faut vérifier avant
 * l'envoi — la personne à contacter (LinkedIn, adresse) et le premier e-mail,
 * modifiable. Le dossier de recherche (comité, offre, signaux) reste replié
 * en dessous. J et K passent d'un dossier à l'autre. Un dossier analysé par
 * l'agent depuis sa dernière ouverture est marqué « Nouveau ».
 */

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LINKEDIN_PATTERN = /^(?:https?:\/\/)?(?:[a-z]{2,3}\.)?(?:www\.)?linkedin\.com\/(?:in|pub|sales\/lead|sales\/people)\/[^\s]+$/i;

/** L'adresse d'un profil collée telle quelle : https, sans paramètres de suivi. */
function cleanLinkedin(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  return withScheme.replace(/^http:/i, "https:").replace(/[?#].*$/, "");
}

async function send(path: string, method: string, body?: unknown) {
  const response = await fetch(path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) throw new Error(typeof data.error === "string" ? data.error : "Action impossible.");
  return data;
}

function errorMessage(cause: unknown) {
  return cause instanceof Error ? cause.message : "Action impossible.";
}

function hostOf(website: string | null) {
  return website ? website.replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/$/, "") : null;
}

/** Ce qui reste à faire sur un dossier, en un mot et un point de couleur. */
function todo(item: ProspectBoardItem): { tone: string; label: string } {
  const pending = item.pending;
  if (!pending || pending.kind !== "sequence") return { tone: "warning", label: "Pas d’e-mail prêt" };
  if (pending.status === "failed") return { tone: "danger", label: "Échec d’envoi" };
  if (pending.status === "held") return { tone: "danger", label: "Retenu par le garde-fou" };
  if (!item.contact_name) return { tone: "warning", label: "Personne à trouver" };
  if (!item.contact_email?.trim()) return { tone: "warning", label: "Adresse à trouver" };
  return { tone: "info", label: "À relire" };
}

/** Le texte que l'on visait dans un champ : J et K n'y naviguent pas. */
function isTyping(target: EventTarget | null) {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName) || target.closest("[role=dialog]") !== null)
  );
}

export function ReviewWorkspace({
  items,
  emailFinder,
  onError,
}: {
  items: ProspectBoardItem[];
  emailFinder: boolean;
  onError: (message: string) => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(items[0]?.id ?? null);
  const [lastIndex, setLastIndex] = useState(0);
  // Ouverts pendant cette visite : la vue ne le saura qu'au prochain rafraîchissement.
  const [opened, setOpened] = useState<ReadonlySet<string>>(() => new Set());
  // Un dossier validé quitte la liste : on reste à la même place.
  const found = items.findIndex((item) => item.id === selectedId);
  const index = found >= 0 ? found : Math.min(lastIndex, items.length - 1);
  const current = items[index];
  const isNew = (item: ProspectBoardItem) => item.unopened && !opened.has(item.id) && item.id !== current?.id;
  const newCount = items.filter(isNew).length;

  // Le dossier affiché est ouvert : il perd sa marque « Nouveau ».
  const currentId = current?.id;
  const currentUnopened = Boolean(current?.unopened);
  useEffect(() => {
    if (!currentId || !currentUnopened) return;
    void send(`/api/admin/prospection/${currentId}/opened`, "POST")
      .then(() => setOpened((previous) => new Set(previous).add(currentId)))
      .catch(() => {});
  }, [currentId, currentUnopened]);

  function select(position: number) {
    const item = items[position];
    if (!item) return;
    setSelectedId(item.id);
    setLastIndex(position);
  }

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey || isTyping(event.target)) return;
      if (event.key === "j") select(index + 1);
      if (event.key === "k") select(index - 1);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (!current) return null;

  return (
    <div className="grid items-start gap-4 lg:grid-cols-[290px_minmax(0,1fr)]">
      <AdminPanel flush>
        <div className="flex items-center justify-between border-b px-4 py-2.5 text-[12.5px] text-muted-foreground">
          <span>
            {items.length} dossier{items.length > 1 ? "s" : ""}
            {newCount > 0 && <span className="font-medium text-foreground"> · {newCount} nouveau{newCount > 1 ? "x" : ""}</span>}
          </span>
          <span className="max-lg:hidden">J / K pour naviguer</span>
        </div>
        <ol aria-label="Dossiers à vérifier" className="m-0 max-h-[70vh] list-none overflow-y-auto p-0 max-lg:max-h-64">
          {items.map((item, position) => {
            const state = todo(item);
            return (
              <li className="border-b last:border-b-0" key={item.id}>
                <button
                  aria-current={position === index ? "true" : undefined}
                  className={cn(
                    "block w-full cursor-pointer px-4 py-2.5 text-left transition-colors hover:bg-muted/60",
                    position === index && "bg-muted",
                  )}
                  onClick={() => select(position)}
                  type="button"
                >
                  <span className="flex gap-3">
                    <OrgLogo className="mt-0.5" name={item.name} size={32} website={item.website} />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="min-w-0 flex-1 truncate font-semibold">{item.contact_name ?? "Personne non trouvée"}</span>
                        {isNew(item) && (
                          <span className="inline-flex flex-none items-center gap-1.5 text-[12px] font-medium">
                            <span aria-hidden className="size-1.5 rounded-full bg-foreground" />
                            Nouveau
                          </span>
                        )}
                      </span>
                      <span className="block truncate text-[12.5px] text-muted-foreground">
                        {[item.name, item.contact_role].filter(Boolean).join(" · ")}
                      </span>
                      <StatusDot className="mt-1" tone={state.tone}>{state.label}</StatusDot>
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </AdminPanel>

      <ReviewDossier
        emailFinder={emailFinder}
        index={index}
        item={current}
        key={current.id}
        onError={onError}
        onNext={() => select(index + 1)}
        onPrevious={() => select(index - 1)}
        // Validé ou écarté : le dossier quitte la liste, on passe au suivant.
        onLeave={() => (items[index + 1] ? select(index + 1) : setSelectedId(null))}
        total={items.length}
      />
    </div>
  );
}

function ReviewDossier({
  item,
  emailFinder,
  index,
  total,
  onPrevious,
  onNext,
  onLeave,
  onError,
}: {
  item: ProspectBoardItem;
  emailFinder: boolean;
  index: number;
  total: number;
  onPrevious: () => void;
  onNext: () => void;
  onLeave: () => void;
  onError: (message: string) => void;
}) {
  const router = useRouter();
  const pending = item.pending?.kind === "sequence" ? item.pending : null;
  const [email, setEmail] = useState(item.contact_email ?? "");
  const [linkedin, setLinkedin] = useState(item.contact_linkedin_url ?? "");
  const [subject, setSubject] = useState(pending?.subject ?? "");
  const [body, setBody] = useState(pending?.body ?? "");
  const [busy, setBusy] = useState<"email" | "linkedin" | "lookup" | "redraft" | "validate" | "dismiss" | null>(null);
  const [copied, setCopied] = useState(false);
  const [researchOpen, setResearchOpen] = useState(false);

  // Ce que le serveur a changé depuis l'ouverture (adresse trouvée par
  // FullEnrich, formule d'appel d'une autre personne retenue) remplace le
  // champ tant qu'on ne l'a pas retouché.
  const [server, setServer] = useState({
    email: item.contact_email ?? "",
    linkedin: item.contact_linkedin_url ?? "",
    subject: pending?.subject ?? "",
    body: pending?.body ?? "",
  });
  const latest = {
    email: item.contact_email ?? "",
    linkedin: item.contact_linkedin_url ?? "",
    subject: pending?.subject ?? server.subject,
    body: pending?.body ?? server.body,
  };
  if (Object.entries(latest).some(([key, value]) => server[key as keyof typeof server] !== value)) {
    if (email.trim() === server.email) setEmail(latest.email);
    if (cleanLinkedin(linkedin) === server.linkedin) setLinkedin(latest.linkedin);
    if (subject === server.subject) setSubject(latest.subject);
    if (body === server.body) setBody(latest.body);
    setServer(latest);
  }

  const cleanEmail = email.trim();
  const validEmail = EMAIL_PATTERN.test(cleanEmail);
  const emailChanged = cleanEmail !== (item.contact_email ?? "");
  const linkedinUrl = cleanLinkedin(linkedin);
  const validLinkedin = linkedinUrl === "" || LINKEDIN_PATTERN.test(linkedinUrl);
  const lookup = item.email_lookup;
  const lookupRunning = lookup?.status === "running";
  const lookupBlocker = !emailFinder
    ? "FullEnrich n’est pas branché : ajoutez FULLENRICH_API_KEY sur le serveur."
    : !item.contact_linkedin_url && !(item.contact_name?.trim().includes(" ") && item.website)
      ? "Renseignez le profil LinkedIn, ou la personne dans la fiche, pour chercher son adresse."
      : null;
  const textChanged = Boolean(pending) && (subject !== (pending?.subject ?? "") || body !== pending?.body);
  const host = hostOf(item.website);

  async function run(key: NonNullable<typeof busy>, action: () => Promise<void>) {
    setBusy(key);
    onError("");
    try {
      await action();
    } catch (cause) {
      onError(errorMessage(cause));
    } finally {
      setBusy(null);
    }
  }

  function saveEmail() {
    if (!emailChanged || (cleanEmail && !validEmail)) return;
    void run("email", async () => {
      await send(`/api/admin/prospection/${item.id}`, "PATCH", { contact_email: cleanEmail || null });
      router.refresh();
    });
  }

  function saveLinkedin(value = linkedin) {
    const url = cleanLinkedin(value);
    if (url === (item.contact_linkedin_url ?? "") || (url && !LINKEDIN_PATTERN.test(url))) return;
    void run("linkedin", async () => {
      await send(`/api/admin/prospection/${item.id}`, "PATCH", { contact_linkedin_url: url || null });
      setLinkedin(url);
      router.refresh();
    });
  }

  function findEmail() {
    void run("lookup", async () => {
      // Un profil collé mais pas encore enregistré sert à la recherche.
      if (linkedinUrl !== (item.contact_linkedin_url ?? "") && validLinkedin) {
        await send(`/api/admin/prospection/${item.id}`, "PATCH", { contact_linkedin_url: linkedinUrl || null });
      }
      await send(`/api/admin/prospection/${item.id}/email-lookup`, "POST");
      router.refresh();
    });
  }

  function validate() {
    if (!pending) return;
    if (pending.status === "held" && !window.confirm("Le garde-fou a retenu cet e-mail. Vous l’avez relu ? Il partira tel quel.")) return;
    void run("validate", async () => {
      if (emailChanged) await send(`/api/admin/prospection/${item.id}`, "PATCH", { contact_email: cleanEmail });
      const text = textChanged ? { subject, body } : {};
      // Déjà validé par la machine (il n'attendait que l'adresse) : seul le texte change.
      if (pending.status === "ready") {
        if (textChanged) await send(`/api/admin/prospection/messages/${pending.id}`, "PATCH", { action: "save", ...text });
      } else {
        await send(`/api/admin/prospection/messages/${pending.id}`, "PATCH", { action: "approve", ...text });
      }
      onLeave();
      router.refresh();
    });
  }

  // Une autre personne retenue, un texte qui ne convient pas : le premier
  // e-mail se réécrit pour l'interlocuteur actuel, sur le même signal.
  function redraft() {
    if (textChanged && !window.confirm("Réécrire le premier e-mail ? Vos retouches seront perdues.")) return;
    void run("redraft", async () => {
      const data = await send(`/api/admin/prospection/${item.id}/email`, "POST", {});
      const message = data.message as { subject: string | null; body: string } | null;
      if (message) {
        setSubject(message.subject ?? "");
        setBody(message.body);
      }
      router.refresh();
    });
  }

  function dismiss() {
    if (!window.confirm(`Écarter ${item.name} ? Son premier e-mail ne partira pas.`)) return;
    void run("dismiss", async () => {
      await send("/api/admin/prospection/bulk", "POST", { action: "dismiss", ids: [item.id] });
      onLeave();
      router.refresh();
    });
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(`${subject}\n\n${body}`);
      setCopied(true);
    } catch {
      onError("La copie a été refusée par le navigateur : sélectionnez le texte à la main.");
    }
  }

  const blocked = !pending
    ? "Aucun premier e-mail prêt : lancez la rédaction."
    : pending.status === "failed"
      ? `Échec d’envoi : ${pending.error ?? "à vérifier"}. Reprenez-le depuis la fiche.`
      : !validEmail
        ? "Renseignez l’adresse de la personne pour valider."
        : null;

  return (
    <div className="grid min-w-0 gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <OrgLogo name={item.name} size={40} website={item.website} />
          <div className="min-w-0">
            <Link className="text-[15px] font-semibold hover:text-foreground" href={`/admin/prospection/${item.id}`}>
              {item.name}
            </Link>
            {host && (
              <a className="ml-2 text-[13px] text-muted-foreground hover:text-foreground" href={item.website ?? "#"} rel="noreferrer" target="_blank">
                {host}
              </a>
            )}
            <small className="block text-[12.5px] text-muted-foreground">
              Dossier {index + 1} sur {total}
              {item.offer_name ? ` · ${item.offer_name}` : ""}
            </small>
          </div>
        </div>
        <span className="flex gap-2">
          <Button disabled={index === 0} onClick={onPrevious} size="sm" type="button" variant="outline">
            <ArrowLeft /> Précédent
          </Button>
          <Button disabled={index >= total - 1} onClick={onNext} size="sm" type="button" variant="outline">
            Suivant <ArrowRight />
          </Button>
        </span>
      </div>

      <AdminPanel flush>
        <section aria-label="Personne à contacter" className="flex flex-wrap items-start gap-4 border-b px-5 py-4.5">
          <ContactAvatar
            className="size-12"
            name={item.contact_name ?? item.name}
            photo={item.contact_photo_url}
            textClassName="text-[15px]"
          />
          <div className="grid min-w-0 flex-[1_1_320px] gap-2.5">
            {item.contact_name ? (
              <div>
                <strong className="block text-[17px] font-semibold">{item.contact_name}</strong>
                <span className="text-muted-foreground">{[item.contact_role, item.name].filter(Boolean).join(" · ")}</span>
              </div>
            ) : (
              <p className="m-0 text-muted-foreground">
                L’agent n’a pas trouvé la personne à contacter.{" "}
                <Link className="font-medium text-primary" href={`/admin/prospection/${item.id}`}>Saisissez-la dans la fiche</Link>,
                sinon l’e-mail commence par « Bonjour, ».
              </p>
            )}
            <div className="grid grid-cols-[64px_minmax(0,1fr)] items-center gap-x-3 gap-y-2 max-sm:grid-cols-1">
              <label className="text-[13px] text-muted-foreground" htmlFor={`linkedin-${item.id}`}>LinkedIn</label>
              <form
                className="flex min-w-0 flex-wrap items-center gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  saveLinkedin();
                }}
              >
                <Input
                  aria-invalid={!validLinkedin ? true : undefined}
                  className="h-8 min-w-[200px] flex-1 text-[13px]"
                  id={`linkedin-${item.id}`}
                  inputMode="url"
                  onBlur={() => saveLinkedin()}
                  onChange={(event) => setLinkedin(event.target.value)}
                  // Coller l'adresse d'un profil suffit : elle s'enregistre aussitôt.
                  onPaste={(event) => {
                    const pasted = event.clipboardData.getData("text");
                    if (!LINKEDIN_PATTERN.test(cleanLinkedin(pasted))) return;
                    event.preventDefault();
                    setLinkedin(cleanLinkedin(pasted));
                    saveLinkedin(pasted);
                  }}
                  placeholder="Collez l’adresse du profil"
                  value={linkedin}
                />
                {busy === "linkedin" && <LoaderCircle aria-label="Enregistrement" className="size-4 animate-spin text-muted-foreground" />}
                {item.contact_linkedin_url ? (
                  <Button asChild size="sm" variant="outline">
                    <a href={item.contact_linkedin_url} rel="noreferrer" target="_blank">
                      <LinkedInIcon /> Ouvrir le profil
                    </a>
                  </Button>
                ) : (
                  <Button asChild size="sm" variant="outline">
                    <a href={linkedinSearchUrl(item)} rel="noreferrer" target="_blank">
                      <LinkedInIcon /> Chercher sur LinkedIn
                    </a>
                  </Button>
                )}
              </form>
              <label className="text-[13px] text-muted-foreground" htmlFor={`email-${item.id}`}>E-mail</label>
              <form
                className="flex min-w-0 flex-wrap items-center gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  saveEmail();
                }}
              >
                <Input
                  aria-invalid={cleanEmail !== "" && !validEmail ? true : undefined}
                  className="h-8 min-w-[200px] flex-1 text-[13px]"
                  id={`email-${item.id}`}
                  onBlur={saveEmail}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="À renseigner"
                  type="email"
                  value={email}
                />
                {busy === "email" && <LoaderCircle aria-label="Enregistrement" className="size-4 animate-spin text-muted-foreground" />}
                {!item.contact_email?.trim() && (
                  <Button
                    disabled={busy !== null || lookupRunning || lookupBlocker !== null}
                    onClick={findEmail}
                    size="sm"
                    title={lookupBlocker ?? "Demande l’adresse professionnelle à FullEnrich (un crédit)"}
                    type="button"
                    variant="outline"
                  >
                    {busy === "lookup" || lookupRunning ? <LoaderCircle className="animate-spin" /> : <MailSearch />}
                    {lookupRunning ? "Recherche en cours…" : "Trouver l’adresse"}
                  </Button>
                )}
              </form>
            </div>
            {!validLinkedin && (
              <p className="m-0 text-[12.5px] text-destructive">Ce n’est pas l’adresse d’un profil LinkedIn (linkedin.com/in/…).</p>
            )}
            {lookupRunning && (
              <p className="m-0 text-[12.5px] text-muted-foreground">
                FullEnrich cherche l’adresse : une à trois minutes, elle s’inscrira ici toute seule.
              </p>
            )}
            {!item.contact_email?.trim() && (lookup?.status === "not_found" || lookup?.status === "failed") && lookup.error && (
              <p className="m-0 text-[12.5px] text-muted-foreground">{lookup.error}</p>
            )}
            {item.contact_reason && (
              <p className="m-0 leading-relaxed text-muted-foreground">Pourquoi cette personne : {item.contact_reason}</p>
            )}
            {item.committee_size > 1 && (
              <Link className="w-fit text-[13px] font-medium text-primary" href={`/admin/prospection/${item.id}`}>
                Autres personnes du comité ({item.committee_size - 1})
              </Link>
            )}
          </div>
        </section>

        <section aria-label="Premier e-mail">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b px-5 py-3">
            <strong className="font-semibold">Premier e-mail</strong>
            <div className="flex flex-wrap items-center gap-3">
              {pending?.status === "held" ? (
                <StatusDot tone="danger">Retenu par le garde-fou : {pending.hold_reason?.replace(/[.\s]+$/, "") ?? "motif non précisé"}</StatusDot>
              ) : pending?.guard_verdict === "pass" ? (
                <StatusDot tone="active">Garde-fou : rien à signaler</StatusDot>
              ) : null}
              <Button
                disabled={busy !== null || pending?.status === "failed"}
                onClick={redraft}
                size="sm"
                title={`Réécrit l’e-mail pour ${item.contact_name?.trim() || "l’interlocuteur actuel"}, sur le même signal`}
                type="button"
                variant="outline"
              >
                {busy === "redraft" ? <LoaderCircle className="animate-spin" /> : <RefreshCw />}
                {busy === "redraft" ? "Rédaction…" : pending ? "Réécrire" : "Rédiger"}
              </Button>
            </div>
          </div>
          {pending ? (
            <div className="grid gap-3 px-5 py-4">
              <div className="grid grid-cols-[56px_minmax(0,1fr)] items-center gap-x-3 gap-y-2.5">
                <span className="text-muted-foreground">À</span>
                {validEmail ? (
                  <span>{item.contact_name ? `${item.contact_name} <${cleanEmail}>` : cleanEmail}</span>
                ) : (
                  <span className="text-warning">Adresse à renseigner ci-dessus</span>
                )}
                <label className="text-muted-foreground" htmlFor={`subject-${item.id}`}>Objet</label>
                <Input className="h-8 font-medium" id={`subject-${item.id}`} onChange={(event) => setSubject(event.target.value)} value={subject} />
              </div>
              <Textarea
                aria-label="Texte du premier e-mail"
                className="min-h-56 text-[13.5px] leading-relaxed"
                onChange={(event) => setBody(event.target.value)}
                value={body}
              />
              <p className="m-0 text-[12.5px] text-muted-foreground">
                {pending.signal ? (
                  <>
                    Signal livré : {pending.signal.organization}
                    {pending.signal.market_clue ? `, ${pending.signal.market_clue.replace(/[.\s]+$/, "")}` : ""}
                    {pending.signal.priority !== null ? ` · ${pending.signal.priority}/100` : ""}
                    {pending.signal.source_link && (
                      <>
                        {" · "}
                        <a className="inline-flex items-center gap-1 font-medium text-primary" href={pending.signal.source_link} rel="noreferrer" target="_blank">
                          voir la source <ExternalLink className="size-3" />
                        </a>
                      </>
                    )}
                    {" · "}
                  </>
                ) : null}
                Le pied (signature, adresse, « répondez stop ») est ajouté à l’envoi.
              </p>
            </div>
          ) : (
            <p className="m-0 px-5 py-4 text-muted-foreground">{blocked}</p>
          )}
          <div className="flex flex-wrap items-center gap-2 border-t px-5 py-3">
            <Button disabled={busy !== null || blocked !== null} onClick={validate} title={blocked ?? undefined} type="button">
              {busy === "validate" ? <LoaderCircle className="animate-spin" /> : <Check />}
              {pending?.status === "ready" ? "Enregistrer et passer au suivant" : "Valider et passer au suivant"}
            </Button>
            {pending && (
              <Button onClick={() => void copy()} type="button" variant="outline">
                <Copy /> {copied ? "Copié" : "Copier pour l’envoyer moi-même"}
              </Button>
            )}
            <span className="flex-1" />
            <Button className="text-muted-foreground" disabled={busy !== null} onClick={dismiss} type="button" variant="ghost">
              {busy === "dismiss" && <LoaderCircle className="animate-spin" />}
              Écarter ce prospect
            </Button>
          </div>
          {pending && blocked && <p className="m-0 border-t px-5 py-2.5 text-[12.5px] text-muted-foreground">{blocked}</p>}
        </section>
      </AdminPanel>

      <Collapsible onOpenChange={setResearchOpen} open={researchOpen}>
        <CollapsibleTrigger className="flex w-full cursor-pointer items-center gap-2 rounded-lg border px-4 py-3 text-left hover:bg-muted/60">
          {researchOpen ? <ChevronDown className="size-4 text-muted-foreground" /> : <ChevronRight className="size-4 text-muted-foreground" />}
          <span className="font-medium">Dossier de recherche</span>
          <span className="text-muted-foreground">
            · comité d’approche, ce qu’ils vendent
            {item.lead_count > 0 ? `, ${item.lead_count} signal${item.lead_count > 1 ? "aux" : ""} trouvé${item.lead_count > 1 ? "s" : ""}` : ""}
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4">{researchOpen && <ResearchDossier id={item.id} />}</CollapsibleContent>
      </Collapsible>
    </div>
  );
}

/** Le dossier de recherche, chargé à l'ouverture : les panneaux de la fiche du prospect. */
function ResearchDossier({ id }: { id: string }) {
  const router = useRouter();
  const { data, error } = useView<{ prospect: ProspectDetail }>(`/api/views/admin/prospection/${id}`);
  const [busy, setBusy] = useState("");
  const [actionError, setActionError] = useState("");

  async function act(key: string, action: () => Promise<unknown>) {
    setBusy(key);
    setActionError("");
    try {
      await action();
      router.refresh();
    } catch (cause) {
      setActionError(errorMessage(cause));
    } finally {
      setBusy("");
    }
  }

  if (error) return <AdminEmpty>Le dossier n’a pas pu être chargé.</AdminEmpty>;
  if (!data) {
    return (
      <p className="m-0 flex items-center gap-2 text-[13px] text-muted-foreground">
        <LoaderCircle className="size-4 animate-spin" /> Chargement du dossier…
      </p>
    );
  }
  return (
    <div className="grid gap-4">
      {actionError && <p className="m-0 text-[13px] text-destructive" role="alert">{actionError}</p>}
      <ContactPanel busy={busy} onAction={act} prospect={data.prospect} />
      <OfferPanel busy={busy} onAction={act} prospect={data.prospect} />
      <LeadsPanel busy={busy} onAction={act} prospect={data.prospect} />
    </div>
  );
}
